/* eslint-disable sonarjs/cognitive-complexity */
/**
 *
 * Component: App
 * Date: 1/12/2021
 *
 */

import React, { useCallback, useEffect, useMemo } from 'react';

import {
  Switch,
  Route,
  BrowserRouter as Router,
  Redirect,
} from 'react-router-dom';

import ErrorBoundary from 'components/ErrorBoundary';
import { Sider, Callback } from 'components';
import { Header } from 'containers';
import { Layout } from 'antd';
import FullStory, { FullStoryAPI } from 'react-fullstory';
// to use intercom uncomment
// import { useIntercom } from 'react-use-intercom';
import { mixpanel } from 'utils/mixpanel';

import { parseJwt, getBaseUrl } from 'utils/commonFunctions';
import HardReset from 'pages/HardReset';
import PopulateLiveViewData from 'components/LiveViewCollector';
import AuthRoute from 'pages/AuthRoute';
import { LiveDataContext } from 'context/liveData';
import Login from 'pages/Login';
import Visuals from 'pages/Visuals';
import Configs from 'pages/Configs';
import Insights from '../Insights';
import Live from '../live';
// import { ProtectedRoute } from './protectedRoute';

import styles from './routes.module.css';

import Auth from '../../auth0-react';

const auth = new Auth();

const App = (): JSX.Element => {
  const { isAuthenticated } = auth;
  const { isUnauthorized } = LiveDataContext();
  // used with intercomm
  // const [userInfo, setUserInfo] = useState<any>({
  //   name: '',
  //   email: '',
  //   domain: '',
  // });
  const domain = useMemo(
    () => localStorage.getItem('domain') || '',
    [localStorage.getItem('domain')],
  );
  const trackUserOnFS = useMemo(() => {
    const accessToken = localStorage.getItem('access_token') || '';
    const parseJwtValue = parseJwt(accessToken);
    if (!parseJwt) return false;
    const aud = Array.isArray(parseJwtValue.aud)
      ? parseJwtValue.aud[0]
      : parseJwtValue.aud;
    return parseJwtValue[`${aud}/trackUserOnFS`] || false;
  }, [localStorage.getItem('access_token')]);

  // Uncomment to add intercom support and import useIntercom
  // const { update } = useIntercom();
  const trackFunction = useCallback(() => {
    const accessTokenStorage = localStorage.getItem('access_token');
    const parseJwtValue = parseJwt(accessTokenStorage || '');
    if (
      (!localStorage.getItem('access_token') || !isAuthenticated()) &&
      !mixpanel.has_opted_out_tracking()
    ) {
      mixpanel.opt_out_tracking();
    }
    if (!parseJwtValue) {
      return;
    }
    const aud = Array.isArray(parseJwtValue.aud)
      ? parseJwtValue.aud[0]
      : parseJwtValue.aud;
    const emailFromToken =
      parseJwtValue[`${aud}/email`] ||
      parseJwtValue['https://vointell.prodigaltech.com/email'];
    if (!emailFromToken || !emailFromToken.split('@')) {
      return;
    }
    const domain1 = emailFromToken.split('@')[1];
    const prodigal = emailFromToken.split('@')[0];
    // uncomment for intercomm support
    // setUserInfo({
    //   ...userInfo,
    //   email: emailFromToken,
    //   domain: domain1,
    //   name: prodigal,
    // });
    try {
      localStorage.setItem('userEmail', emailFromToken);
      localStorage.setItem('user_name', prodigal);
      localStorage.setItem('domain', domain1);
    } catch (error) {
      // TODO: move to common function
    }
    if (
      domain &&
      domain !== 'prodigaltech.com' &&
      window.location.host.includes('prodigaltech') // for notesintel.prodigaltech.com
    ) {
      if (mixpanel.has_opted_out_tracking()) {
        mixpanel.opt_in_tracking();
        let ip = '';
        try {
          ip = localStorage.getItem('ipv4') as string;
        } catch {
          // TODO: move to common function
        }
        mixpanel.identify(emailFromToken);
        mixpanel.register({
          user_name: prodigal,
          email: emailFromToken,
          version: '0.0.1',
          server: getBaseUrl(),
          ip,
          domain:
            domain.split('.') && domain.split('.').length > 0
              ? domain.split('.')[0]
              : domain,
        });
      }
      if (trackUserOnFS) {
        FullStoryAPI('identify', emailFromToken);
        FullStoryAPI('setUserVars', {
          displayName: prodigal,
          email: emailFromToken,
        });
      }
    } else if (!mixpanel.has_opted_out_tracking()) {
      mixpanel.opt_out_tracking();
    }
  }, []);

  useEffect(() => {
    trackFunction();
  }, [localStorage.getItem('access_token')]);

  // For adding intercom support uncomment.
  // useEffect(() => {
  //   if (userInfo.name)
  //     update({
  //       name: userInfo.name,
  //       user_id: userInfo.email,
  //       email: userInfo.email,
  //     } as any);
  // }, [userInfo.email, userInfo.name, update]);

  return (
    <Layout style={{ minHeight: '100vh', width: '100%' }}>
      {domain &&
        trackUserOnFS &&
        window.location.host.includes('prodigaltech.com') && (
          <FullStory org="D4JS9" />
          // eslint-disable-next-line @typescript-eslint/indent
        )}
      <ErrorBoundary>
        <div className={styles.container}>
          <div className={styles.routesDiv}>
            <Router>
              <Switch>
                <AuthRoute exact path="/login" component={Login} />
                <Route exact path="/callback" render={() => <Callback />} />
                <Route exact path="/hardreset" component={HardReset} />
                <>
                  <Sider />
                  <section
                    className={styles.fixedSiderContentLayout}
                    style={{ overflowY: 'hidden' }}
                  >
                    <Header />
                    <div className={styles.routesComponents}>
                      {isAuthenticated() && !isUnauthorized ? (
                        <PopulateLiveViewData />
                      ) : null}
                      <Switch>
                        <AuthRoute exact path="/live" component={Live} />
                        <AuthRoute
                          exact
                          path="/insights"
                          component={Insights}
                        />
                        <AuthRoute exact path="/visuals" component={Visuals} />
                        <AuthRoute
                          exact
                          path="/visuals/:id"
                          component={Visuals}
                        />
                        <AuthRoute
                          exact
                          path="/configs/visualsConfiguration"
                          component={Configs}
                        />
                        <Redirect to="/live" />
                      </Switch>
                    </div>
                  </section>
                </>
              </Switch>
            </Router>
          </div>
        </div>
      </ErrorBoundary>
    </Layout>
  );
};
export default App;
